<template>
  <div class="promotion-participant-picker-root">
  <CustomDialog
    v-model="dialogModel"
    :footer="false"
    :is_translate="false"
    :title="dialogTitle"
    width="760px"
  >
    <div slot="activator" class="participant-dialog">
      <div class="participant-search">
        <label :for="searchInputId" class="sr-only">{{ $t(searchPlaceholder) }}</label>
        <input
          :id="searchInputId"
          v-model="search"
          type="text"
          class="participant-search-input"
          :placeholder="$t(searchPlaceholder)"
          @keyup.enter="fetchItems"
        />
        <button
          type="button"
          class="participant-search-btn"
          :aria-label="$t('search')"
          @click="fetchItems"
        >
          <v-icon small color="white">fal fa-search</v-icon>
        </button>
      </div>

      <div class="participant-toolbar">
        <v-checkbox
          dense
          hide-details
          color="primary"
          :input-value="allVisibleSelected"
          :indeterminate="someVisibleSelected && !allVisibleSelected"
          :label="$t('select_all_visible')"
          @change="toggleSelectAllVisible"
        />
        <div class="participant-count">
          {{ $t("promotion_selected_count", { count: internalSelectedIds.length }) }}
        </div>
      </div>

      <div v-if="loading" class="participant-body">
        <div v-for="n in 4" :key="`skeleton-${n}`" class="participant-skeleton-row">
          <div class="skeleton-block skeleton-icon"></div>
          <div class="skeleton-block skeleton-title"></div>
          <div class="skeleton-block skeleton-count"></div>
        </div>
      </div>

      <div v-else-if="!normalizedItems.length" class="participant-empty">
        {{ $t("promotion_no_data_found") }}
      </div>

      <div v-else class="participant-body">
        <div v-if="mode === 'category'" class="participant-list">
          <div
            v-for="item in normalizedItems"
            :key="`category-${item.id}`"
            class="participant-row"
            :class="{ selected: internalSelectedIds.includes(item.id) }"
            @click="toggleItem(item)"
          >
            <v-checkbox
              dense
              hide-details
              color="primary"
              :input-value="internalSelectedIds.includes(item.id)"
              class="participant-row-checkbox"
              @click.stop
              @change="toggleItem(item)"
            />
            <div class="participant-content">
              <div class="participant-title">{{ item.label }}</div>
              <div class="participant-subtitle">
                {{ $t("promotion_category_id", { id: item.id }) }}
              </div>
            </div>
          </div>
        </div>

        <div v-else class="participant-groups">
          <div
            v-for="group in groupedProducts"
            :key="group.name"
            class="participant-group"
          >
            <div class="participant-group-header" @click="toggleGroupExpanded(group.name)">
              <v-checkbox
                dense
                hide-details
                color="primary"
                :input-value="groupSelectionState(group).allSelected"
                :indeterminate="groupSelectionState(group).indeterminate"
                class="participant-row-checkbox"
                @click.stop
                @change="toggleGroupSelection(group)"
              />
              <v-icon small color="#438ef2" class="participant-group-icon"
                >fal fa-utensils</v-icon
              >
              <span class="participant-group-name">{{ group.name }}</span>
              <span class="participant-group-count">
                {{ $t("promotion_items_count", { count: group.items.length }) }}
              </span>
              <v-icon small class="participant-group-chevron">
                {{ isGroupExpanded(group.name) ? "fal fa-chevron-up" : "fal fa-chevron-down" }}
              </v-icon>
            </div>

            <div v-show="isGroupExpanded(group.name)" class="participant-group-body">
              <div
                v-for="item in group.items"
                :key="`product-${item.id}`"
                class="participant-row"
                :class="{
                  selected: internalSelectedIds.includes(item.id),
                  disabled: isItemUnselectable(item),
                }"
                @click="toggleItem(item)"
              >
                <v-checkbox
                  dense
                  hide-details
                  color="primary"
                  :disabled="isItemUnselectable(item)"
                  :input-value="internalSelectedIds.includes(item.id)"
                  class="participant-row-checkbox"
                  @click.stop
                  @change="toggleItem(item)"
                />
                <div class="participant-image">
                  <v-img v-if="item.image" :src="item.imageUrl" width="36" height="36" cover />
                  <v-icon v-else small color="#cbd5e1">fal fa-image</v-icon>
                </div>
                <div class="participant-content">
                  <div class="participant-title">{{ item.label }}</div>
                  <div v-if="item.code" class="participant-subtitle">{{ item.code }}</div>
                  <div v-if="isItemUnselectable(item)" class="participant-subtitle participant-no-variant">
                    {{ $t("promotion_no_variant_available") }}
                  </div>
                  <div
                    v-if="item.variants && item.variants.length"
                    class="participant-variant-row"
                  >
                    <span class="participant-variant-summary">
                      {{ variantSummaryLabel(item) }}
                    </span>
                    <button
                      type="button"
                      class="participant-variant-edit-btn"
                      @click.stop="openVariantPicker(item)"
                    >
                      <v-icon x-small color="#438ef2">fal fa-pen</v-icon>
                      {{ $t("edit_variants") }}
                    </button>
                  </div>
                </div>
                <div v-if="item.priceSale !== null" class="participant-price">
                  {{ formatPrice(item.priceSale) }} {{ item.currencyCode }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="participant-actions">
        <v-btn
          depressed
          color="#DDEAFD"
          class="text-none primary--text rounded-pill participant-action-btn"
          @click="closeDialog"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          class="text-none rounded-pill participant-action-btn"
          @click="saveSelection"
        >
          {{ $t("save") }}
        </v-btn>
      </div>
    </div>
  </CustomDialog>

  <v-dialog v-model="variantDialog" max-width="440" content-class="rounded-lg">
    <v-card v-if="variantDialogItem" class="pa-2">
      <v-card-title class="font-weight-bold">
        {{ $t("select_variant") }}
        <div class="variant-dialog-subtitle">{{ variantDialogItem.label }}</div>
      </v-card-title>
      <v-card-text>
        <div class="variant-hint">{{ $t("promotion_variant_hint") }}</div>
        <div class="variant-option-list">
          <div
            v-for="variant in variantDialogItem.variants"
            :key="`variant-${variant.id}`"
            class="variant-option-row"
            :class="{ selected: variantSelectedIds.includes(variant.id) }"
            @click="toggleVariantOption(variant.id)"
          >
            <v-checkbox
              dense
              hide-details
              color="primary"
              :input-value="variantSelectedIds.includes(variant.id)"
              class="participant-row-checkbox"
              @click.stop
              @change="toggleVariantOption(variant.id)"
            />
            <div class="participant-content">
              <div class="participant-title">{{ variant.label }}</div>
              <div v-if="variant.sku" class="participant-subtitle">{{ variant.sku }}</div>
            </div>
            <div v-if="variant.price !== null" class="participant-price">
              {{ formatPrice(variant.price) }}
            </div>
          </div>
        </div>
      </v-card-text>
      <v-card-actions class="pb-4 px-4">
        <v-spacer />
        <v-btn
          depressed
          color="#DDEAFD"
          class="text-none primary--text rounded-pill"
          @click="cancelVariantPicker"
        >
          {{ $t("cancel") }}
        </v-btn>
        <v-btn
          depressed
          color="primary"
          class="text-none rounded-pill"
          @click="confirmVariantPicker"
        >
          {{ $t("save") }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
  </div>
</template>

<script>
import CustomDialog from "@/components/customDialog.vue";
import Help from "@/utils/helper";
import {
  normalizeCategoryItem,
  normalizeCollection,
  normalizeProductItem,
} from "@/utils/promotions";

export default {
  name: "PromotionParticipantPicker",
  components: {
    CustomDialog,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: "category",
    },
    selectedIds: {
      type: Array,
      default: () => [],
    },
    selectedItems: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      search: "",
      loading: false,
      internalSelectedIds: [],
      normalizedItems: [],
      itemCache: {},
      expandedGroups: [],
      variantDialog: false,
      variantDialogItem: null,
      variantSelectedIds: [],
    };
  },
  computed: {
    searchInputId() {
      return `participant-search-${this._uid}`;
    },
    dialogModel: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit("input", value);
      },
    },
    dialogTitle() {
      return this.mode === "product"
        ? this.$t("select_products")
        : this.$t("select_categories");
    },
    searchPlaceholder() {
      return this.mode === "product"
        ? "participant_search_products"
        : "participant_search_categories";
    },
    selectableItems() {
      return this.normalizedItems.filter((item) => !this.isItemUnselectable(item));
    },
    allVisibleSelected() {
      if (!this.selectableItems.length) return false;
      return this.selectableItems.every((item) =>
        this.internalSelectedIds.includes(item.id)
      );
    },
    someVisibleSelected() {
      return this.selectableItems.some((item) =>
        this.internalSelectedIds.includes(item.id)
      );
    },
    groupedProducts() {
      const groups = {};
      this.normalizedItems.forEach((item) => {
        const groupName = item.categoryLabel || this.$t("products");
        if (!groups[groupName]) {
          groups[groupName] = [];
        }
        groups[groupName].push({
          ...item,
          imageUrl: item.image ? Help.img(item.image) : "",
        });
      });

      return Object.keys(groups)
        .sort((a, b) => a.localeCompare(b))
        .map((name) => ({
          name,
          items: groups[name],
        }));
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.initializeDialog();
      }
    },
    mode() {
      if (this.value) {
        this.initializeDialog();
      }
    },
  },
  methods: {
    formatPrice(value) {
      const num = Number(value) || 0;

      return num.toLocaleString("en-US", { maximumFractionDigits: 2 });
    },
    initializeDialog() {
      this.search = "";
      this.internalSelectedIds = [...this.selectedIds];
      this.itemCache = {};
      this.expandedGroups = [];
      this.selectedItems.forEach((item) => {
        if (item && item.id) {
          this.itemCache[item.id] = item;
        }
      });
      this.fetchItems();
    },
    async fetchItems() {
      this.loading = true;
      try {
        const query = this.search ? `q=${encodeURIComponent(this.search)}` : "";
        const separator = query ? "&" : "";
        const endpoint = this.mode === "product" ? "products_all" : "categories_paginate";
        const url = `${endpoint}?${query}${separator}per_page=100`;
        const { data } = await this.$http.get(url);
        const items = normalizeCollection(data?.data);
        const locale = this.$i18n.locale;
        this.normalizedItems = items.map((item) =>
          this.mode === "product"
            ? normalizeProductItem(item, locale)
            : normalizeCategoryItem(item)
        );

        this.normalizedItems.forEach((item) => {
          const existing = this.itemCache[item.id];
          this.itemCache[item.id] =
            existing && Array.isArray(existing.variantIds)
              ? { ...item, variantIds: existing.variantIds }
              : item;
        });
      } catch (error) {
        console.error("Failed to load promotion participants", error);
        this.normalizedItems = [];
      } finally {
        this.loading = false;
      }
    },
    isItemUnselectable(item) {
      return (
        this.mode === "product" &&
        item.hasVariations &&
        (!Array.isArray(item.variants) || !item.variants.length)
      );
    },
    toggleItem(item) {
      if (this.isItemUnselectable(item)) return;

      const exists = this.internalSelectedIds.includes(item.id);
      if (exists) {
        this.internalSelectedIds = this.internalSelectedIds.filter(
          (id) => id !== item.id
        );
        if (this.itemCache[item.id]) {
          this.itemCache[item.id] = { ...this.itemCache[item.id], variantIds: [] };
        }
        return;
      }

      if (this.mode === "product" && Array.isArray(item.variants) && item.variants.length) {
        this.openVariantPicker(item);
        return;
      }

      this.internalSelectedIds = [...this.internalSelectedIds, item.id];
      this.itemCache[item.id] = item;
    },
    openVariantPicker(item) {
      const cached = this.itemCache[item.id];
      this.variantDialogItem = item;
      this.variantSelectedIds =
        cached && Array.isArray(cached.variantIds) ? [...cached.variantIds] : [];
      this.variantDialog = true;
    },
    toggleVariantOption(variantId) {
      const exists = this.variantSelectedIds.includes(variantId);
      this.variantSelectedIds = exists
        ? this.variantSelectedIds.filter((id) => id !== variantId)
        : [...this.variantSelectedIds, variantId];
    },
    cancelVariantPicker() {
      this.variantDialog = false;
      this.variantDialogItem = null;
    },
    confirmVariantPicker() {
      const item = this.variantDialogItem;
      if (!item) return;

      this.itemCache[item.id] = { ...item, variantIds: [...this.variantSelectedIds] };
      if (!this.internalSelectedIds.includes(item.id)) {
        this.internalSelectedIds = [...this.internalSelectedIds, item.id];
      }
      this.variantDialog = false;
      this.variantDialogItem = null;
    },
    variantSummaryLabel(item) {
      const cached = this.itemCache[item.id];
      const total = item.variants ? item.variants.length : 0;
      const chosen = cached && Array.isArray(cached.variantIds) ? cached.variantIds.length : 0;
      if (!chosen) return this.$t("promotion_all_variants");
      return this.$t("promotion_variants_selected_count", { count: chosen, total });
    },
    // A product with variants can't be bulk-added: there's no popup here to
    // scope which variant(s) it applies to, so silently adding it would mean
    // "every variant" without the user ever choosing that. Those still have
    // to go through the single-click flow, which forces openVariantPicker.
    isBulkAddable(item) {
      return (
        !this.isItemUnselectable(item) &&
        !(this.mode === "product" && Array.isArray(item.variants) && item.variants.length)
      );
    },
    toggleSelectAllVisible() {
      const selectableItems = this.normalizedItems.filter(
        (item) => !this.isItemUnselectable(item)
      );
      const visibleIds = new Set(selectableItems.map((item) => item.id));
      if (this.allVisibleSelected) {
        this.internalSelectedIds = this.internalSelectedIds.filter(
          (id) => !visibleIds.has(id)
        );
        return;
      }

      const addableItems = selectableItems.filter((item) => this.isBulkAddable(item));
      this.internalSelectedIds = Array.from(
        new Set([...this.internalSelectedIds, ...addableItems.map((item) => item.id)])
      );
      addableItems.forEach((item) => {
        this.itemCache[item.id] = item;
      });
    },
    isGroupExpanded(name) {
      return this.expandedGroups.includes(name);
    },
    toggleGroupExpanded(name) {
      if (this.isGroupExpanded(name)) {
        this.expandedGroups = this.expandedGroups.filter((n) => n !== name);
        return;
      }
      this.expandedGroups = [...this.expandedGroups, name];
    },
    groupSelectionState(group) {
      const selectableItems = group.items.filter((item) => !this.isItemUnselectable(item));
      const ids = selectableItems.map((item) => item.id);
      const selectedCount = ids.filter((id) =>
        this.internalSelectedIds.includes(id)
      ).length;
      return {
        allSelected: ids.length > 0 && selectedCount === ids.length,
        indeterminate: selectedCount > 0 && selectedCount < ids.length,
      };
    },
    toggleGroupSelection(group) {
      const selectableItems = group.items.filter((item) => !this.isItemUnselectable(item));
      const ids = new Set(selectableItems.map((item) => item.id));
      const { allSelected } = this.groupSelectionState(group);
      if (allSelected) {
        this.internalSelectedIds = this.internalSelectedIds.filter(
          (id) => !ids.has(id)
        );
        return;
      }
      const addableItems = selectableItems.filter((item) => this.isBulkAddable(item));
      this.internalSelectedIds = Array.from(
        new Set([...this.internalSelectedIds, ...addableItems.map((item) => item.id)])
      );
      addableItems.forEach((item) => {
        this.itemCache[item.id] = item;
      });
    },
    closeDialog() {
      this.dialogModel = false;
    },
    saveSelection() {
      // itemCache should always have every selected id by now, but fall back
      // to the originally-passed-in selectedItems prop before giving up and
      // showing a bare "#id" — losing that item's real label/variantIds here
      // would silently widen its promotion scope back to "whole product".
      const items = this.internalSelectedIds.map(
        (id) =>
          this.itemCache[id] ||
          this.selectedItems.find((item) => item.id === id) || { id, label: `#${id}` }
      );
      this.$emit("save", {
        ids: [...this.internalSelectedIds],
        items,
      });
      this.closeDialog();
    },
  },
};
</script>

<style scoped>
.participant-dialog {
  min-height: 420px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.participant-search {
  display: flex;
  align-items: stretch;
  gap: 8px;
  margin-bottom: 16px;
}

.participant-search-input {
  flex: 1;
  min-width: 0;
  height: 44px;
  padding: 0 16px;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  background: #f8fafc;
  font-size: 14px;
  color: #0f172a;
  outline: none;
  transition: border-color 0.2s ease;
}

.participant-search-input::placeholder {
  color: #94a3b8;
}

.participant-search-input:focus {
  border-color: #438ef2;
  background: #fff;
}

.participant-search-btn {
  width: 44px;
  height: 44px;
  flex: none;
  border-radius: 50%;
  border: none;
  background: #438ef2;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
}

.participant-search-btn:hover {
  background: #3a7fd8;
}

.participant-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #eef2f7;
}

.participant-count {
  color: #1d4ed8;
  font-size: 12px;
  font-weight: 700;
  background: #eaf3ff;
  padding: 4px 12px;
  border-radius: 999px;
}

.participant-empty {
  min-height: 240px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #64748b;
}

.participant-body {
  max-height: 380px;
  overflow-y: auto;
  padding-right: 4px;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 transparent;
}

.participant-body::-webkit-scrollbar {
  width: 6px;
}

.participant-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 999px;
}

.participant-body::-webkit-scrollbar-track {
  background: transparent;
}

.participant-list,
.participant-groups {
  margin-top: 16px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.participant-group {
  border-radius: 14px;
  background: #f8fafc;
  overflow: hidden;
}

.participant-group-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 14px;
  cursor: pointer;
}

.participant-row-checkbox {
  margin: 0;
  padding: 0;
  flex: none;
}

.participant-row-checkbox >>> .v-input__slot {
  margin-bottom: 0;
}

.participant-row-checkbox >>> .v-messages {
  display: none;
}

.participant-group-icon {
  flex: none;
}

.participant-group-name {
  flex: 1;
  min-width: 0;
  font-weight: 700;
  color: #0f172a;
  font-size: 14px;
}

.participant-group-count {
  color: #438ef2;
  font-size: 13px;
  font-weight: 600;
  margin-right: 6px;
}

.participant-group-chevron {
  flex: none;
}

.participant-group-body {
  padding: 0 14px 10px 14px;
}

.participant-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.participant-row:hover {
  background: #fff;
  border-color: #e2e8f0;
}

.participant-row.selected {
  background: #fff;
  border-color: #bcdcff;
}

.participant-row.disabled {
  opacity: 0.5;
  cursor: not-allowed;
  filter: grayscale(0.3);
}

.participant-row.disabled:hover {
  background: transparent;
  border-color: transparent;
}

.participant-no-variant {
  color: #ef4444;
}

.participant-image {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f5f9;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.participant-content {
  flex: 1;
  min-width: 0;
}

.participant-price {
  flex: none;
  color: #1d4ed8;
  font-weight: 700;
  font-size: 13px;
  white-space: nowrap;
}

.participant-title {
  color: #1e293b;
  font-weight: 600;
  font-size: 14px;
  line-height: 1.3;
}

.participant-subtitle {
  color: #64748b;
  font-size: 12px;
  margin-top: 2px;
}

.participant-variant-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
}

.participant-variant-summary {
  font-size: 11px;
  color: #438ef2;
  font-weight: 600;
}

.participant-variant-edit-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  color: #438ef2;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
}

.variant-dialog-subtitle {
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  margin-top: 2px;
}

.variant-hint {
  font-size: 12px;
  color: #64748b;
  margin-bottom: 12px;
}

.variant-option-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  max-height: 320px;
  overflow-y: auto;
}

.variant-option-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 10px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease;
}

.variant-option-row:hover {
  background: #f8fafc;
}

.variant-option-row.selected {
  background: #eaf3ff;
  border-color: #bcdcff;
}

.participant-skeleton-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  margin-top: 10px;
  border-radius: 14px;
  background: #f8fafc;
}

.skeleton-block {
  border-radius: 6px;
  background: linear-gradient(90deg, #eef1f5 25%, #f7f9fb 37%, #eef1f5 63%);
  background-size: 400% 100%;
  animation: participant-skeleton-shimmer 1.4s ease infinite;
}

.skeleton-icon {
  width: 20px;
  height: 20px;
  border-radius: 50%;
}

.skeleton-title {
  flex: 1;
  height: 14px;
}

.skeleton-count {
  width: 60px;
  height: 12px;
}

@keyframes participant-skeleton-shimmer {
  0% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0 50%;
  }
}

.participant-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 20px;
}

.participant-action-btn {
  height: 44px !important;
  padding: 0 24px !important;
  font-weight: 700;
  box-shadow: none !important;
}

@media (max-width: 600px) {
  .participant-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }

  .participant-actions {
    flex-direction: column;
  }
}

.theme--dark .participant-search-input {
  background: var(--app-input-bg, #1e293b);
  border-color: rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .participant-search-input:focus {
  background: var(--app-input-bg, #1e293b);
}

.theme--dark .participant-toolbar {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.theme--dark .participant-group,
.theme--dark .participant-image,
.theme--dark .variant-option-row:hover {
  background: rgba(255, 255, 255, 0.06);
}

.theme--dark .participant-group-name,
.theme--dark .participant-title {
  color: rgba(255, 255, 255, 0.87);
}

.theme--dark .participant-row:hover,
.theme--dark .participant-row.selected {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.16);
}

.theme--dark .participant-skeleton-row {
  background: rgba(255, 255, 255, 0.06);
}

.theme--dark .participant-count {
  background: rgba(67, 142, 242, 0.18);
  color: #90caf9;
}

.theme--dark .participant-price {
  color: #90caf9;
}

.theme--dark .skeleton-block {
  background: linear-gradient(90deg, #262626 25%, #333 37%, #262626 63%);
  background-size: 400% 100%;
}
</style>
